"use client";

import * as React from "react";
import { PHONE_QUERY } from "@bastion/ui";

export interface SidebarContextType {
  /** Whether the sidebar is expanded (full width) or collapsed (icon only) */
  isExpanded: boolean;
  /** Toggle the sidebar expansion state */
  toggle: () => void;
  /** Set the sidebar expansion state directly */
  setIsExpanded: (expanded: boolean) => void;
  /** Set of expanded section IDs */
  expandedSections: Set<string>;
  /** Toggle a section's expanded state */
  toggleSection: (sectionId: string) => void;
  /** Expand a specific section */
  expandSection: (sectionId: string) => void;
  /** Custom width set by resize drag (null = use default CSS width) */
  customWidth: number | null;
  /** Set custom width (null to reset to default) */
  setCustomWidth: (width: number | null) => void;
  /** Whether the sidebar is currently being resized */
  isResizing: boolean;
  /** Set resizing state */
  setIsResizing: (resizing: boolean) => void;
  /** Whether the viewport is at phone width (≤760px, PHONE_QUERY) */
  isMobile: boolean;
}

const SidebarContext = React.createContext<SidebarContextType | undefined>(
  undefined
);

interface SidebarProviderProps {
  children: React.ReactNode;
  /** localStorage key for persisting state */
  persistKey?: string;
  /** Default expanded state */
  defaultExpanded?: boolean;
}

export function SidebarProvider({
  children,
  persistKey = "sidebar",
  defaultExpanded = true,
}: SidebarProviderProps) {
  const [mounted, setMounted] = React.useState(false);
  // On mobile, always start collapsed (sidebar is an overlay)
  const [isExpanded, setIsExpanded] = React.useState(() => {
    if (typeof window === "undefined") return defaultExpanded;
    return window.matchMedia(PHONE_QUERY).matches ? false : defaultExpanded;
  });
  const [expandedSections, setExpandedSections] = React.useState<Set<string>>(
    new Set()
  );
  const [customWidth, setCustomWidth] = React.useState<number | null>(null);
  const [isResizing, setIsResizing] = React.useState(false);
  // Initialize isMobile synchronously so it's correct from the first render
  const [isMobile, setIsMobile] = React.useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(PHONE_QUERY).matches;
  });

  // Track mobile breakpoint changes via matchMedia listener
  React.useEffect(() => {
    const mql = window.matchMedia(PHONE_QUERY);
    const handleChange = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
      // Auto-collapse sidebar when entering mobile
      if (e.matches) {
        setIsExpanded(false);
      }
    };
    mql.addEventListener("change", handleChange);
    return () => mql.removeEventListener("change", handleChange);
  }, []);

  // Load from localStorage after mount to avoid hydration mismatch
  React.useEffect(() => {
    // On mobile, don't restore expanded state from localStorage — sidebar
    // should always start collapsed (closed overlay) on mobile viewports
    const mobile = window.matchMedia(PHONE_QUERY).matches;
    const savedExpanded = localStorage.getItem(`${persistKey}-expanded`);
    if (savedExpanded !== null && !mobile) {
      setIsExpanded(savedExpanded === "true");
    }

    const savedSections = localStorage.getItem(`${persistKey}-sections`);
    if (savedSections) {
      try {
        setExpandedSections(new Set(JSON.parse(savedSections)));
      } catch {
        // ignore parsing errors
      }
    }

    const savedWidth = localStorage.getItem(`${persistKey}-width`);
    if (savedWidth !== null) {
      const parsed = Number(savedWidth);
      if (!isNaN(parsed) && parsed > 0) {
        setCustomWidth(parsed);
      }
    }

    setMounted(true);
  }, [persistKey]);

  // Save expansion state to localStorage
  React.useEffect(() => {
    if (!mounted) return;
    localStorage.setItem(`${persistKey}-expanded`, String(isExpanded));
  }, [isExpanded, mounted, persistKey]);

  // Save sections state to localStorage
  React.useEffect(() => {
    if (!mounted) return;
    localStorage.setItem(
      `${persistKey}-sections`,
      JSON.stringify([...expandedSections])
    );
  }, [expandedSections, mounted, persistKey]);

  // Save custom width to localStorage
  React.useEffect(() => {
    if (!mounted) return;
    if (customWidth !== null) {
      localStorage.setItem(`${persistKey}-width`, String(customWidth));
    } else {
      localStorage.removeItem(`${persistKey}-width`);
    }
  }, [customWidth, mounted, persistKey]);

  const toggle = React.useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const toggleSection = React.useCallback((sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  const expandSection = React.useCallback((sectionId: string) => {
    setExpandedSections((prev) => {
      if (prev.has(sectionId)) return prev;
      return new Set([...prev, sectionId]);
    });
  }, []);

  const value = React.useMemo(
    () => ({
      isExpanded,
      toggle,
      setIsExpanded,
      expandedSections,
      toggleSection,
      expandSection,
      customWidth,
      setCustomWidth,
      isResizing,
      setIsResizing,
      isMobile,
    }),
    [isExpanded, toggle, expandedSections, toggleSection, expandSection, customWidth, isResizing, isMobile]
  );

  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
}

export { SidebarContext };
